import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import sortBy from 'lodash.sortby';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LatestDomainSettings } from '../../../api/types';
import { Domains } from '../../../constants';
import {
  useAllLatestDomainSettingsContext,
  useLocalStorageDomainServiceContext,
  useOriginalRoleViewAuthorizationServiceContext,
} from '../../../context/contexts';

export const WhiteTextField = styled(TextField)`
  input {
    color: #fff;
  }
  & .MuiInput-underline::before {
    border-bottom-color: #fff;
  }
  & .MuiInput-underline::after {
    border-bottom-color: #fff;
  }
  & .MuiInput-underline:hover::before {
    border-bottom-color: #fff;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  && {
    padding: 0px;
  }
`;

const StyledDialog = styled(Dialog)`
  & .paper {
    background: ${(props) => props.theme.color.primary};
    width: 50vw;
    height: 80vh;
  }
`;

const StyledAvatar = styled(Avatar)`
  border-radius: 0;
  background: transparent;
`;

interface DomainSelectorProps {
  selectedDomain: Domains | null;
  setSelectedDomain: (domain: Domains) => void;
}

export const DomainSelector = (props: DomainSelectorProps) => {
  const localStorageDomainService = useLocalStorageDomainServiceContext();
  const authorizationService = useOriginalRoleViewAuthorizationServiceContext();
  const { selectedDomain, setSelectedDomain } = props;
  const { t } = useTranslation();
  const [searched, setSearched] = useState('');

  const availableDomainSettings: LatestDomainSettings[] =
    useAllLatestDomainSettingsContext();

  const onDomainSelect = (domain: Domains) => () => {
    setSelectedDomain(domain);
    localStorageDomainService.set(domain);
  };

  const isPanalytSuperAdmin = authorizationService.canManageAllDomains();

  const sortedDomainSettings = sortBy(
    availableDomainSettings.map((d) => d.settings),
    (d) => d.name
  );
  const filteredDomainSettings = sortedDomainSettings.filter((d) => {
    const textToSearch = [d.domain, d.domainOfPartner, d.name, d.alias].join(
      '   '
    );
    return textToSearch.toLowerCase().includes(searched.toLowerCase());
  });

  return (
    <StyledDialog
      aria-labelledby="simple-dialog-title"
      open
      classes={{ paper: 'paper' }}
      data-cy="domain_selector"
    >
      <DialogTitle id="simple-dialog-title">
        <Typography
          variant="h6"
          sx={{
            color: 'secondary.main',
          }}
        >
          {t('common:shell.selectDomain')}
        </Typography>
      </DialogTitle>
      <StyledDialogContent>
        {sortedDomainSettings.length === 0 ? (
          <Grid
            container
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {/* TODO: add error couldn't load domain settings */}
          </Grid>
        ) : (
          <Grid
            container
            direction="column"
            sx={{
              alignItems: 'center',
            }}
          >
            <Grid
              item
              container
              sx={{
                justifyContent: 'center',
                alignItems: 'flex-end',
              }}
            >
              <SearchIcon sx={{ color: '#fff' }} />
              <WhiteTextField
                sx={{ width: '50%' }}
                value={searched}
                onChange={(e) => setSearched(e.target.value)}
                autoFocus
              />
            </Grid>
            <List data-cy="domain_selector_list" sx={{ width: '100%' }}>
              {filteredDomainSettings.map((domainSettings) => {
                const { domain, alias, name, logoURL } = domainSettings;
                const domainDisplayNameText = alias
                  ? `${name} ( ${alias} )`
                  : name;
                const domainDisplayNameNormalUser = alias ?? name;
                const domainDisplayNamePsa = alias ? (
                  <>
                    {' '}
                    {name} <i>{alias}</i>{' '}
                  </>
                ) : (
                  name
                );
                const domainDisplayNameComponent = isPanalytSuperAdmin
                  ? domainDisplayNamePsa
                  : domainDisplayNameNormalUser;
                return (
                  <ListItemButton
                    onClick={onDomainSelect(domain)}
                    key={domain}
                    selected={domain === selectedDomain}
                  >
                    <ListItemAvatar>
                      {logoURL ? (
                        <StyledAvatar src={logoURL} />
                      ) : (
                        <StyledAvatar>
                          {domainDisplayNameText &&
                            domainDisplayNameText.charAt(0).toUpperCase()}
                        </StyledAvatar>
                      )}
                    </ListItemAvatar>
                    <ListItemText disableTypography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'secondary.main',
                        }}
                      >
                        {domainDisplayNameComponent}
                      </Typography>
                    </ListItemText>
                  </ListItemButton>
                );
              })}
            </List>
          </Grid>
        )}
      </StyledDialogContent>
    </StyledDialog>
  );
};
